export function constructVirtualControlsPrompt(
  aiStrength: number,
  resemblance: number,
  clarity: number,
  sharpness: number
): string {
  const sharpnessValue = (sharpness / 100).toFixed(2);

  return `Objective:
You are an advanced AI image enhancement and generation engine. Your primary function is to process an uploaded image based on a set of precise control parameters defined below. These parameters act as virtual sliders, giving nuanced control over the final output. You must adhere strictly to the values provided for each parameter in every generation.

[--VIRTUAL TOOLSET CONTROLS--]
1. AI Strength: [Value: ${aiStrength}%]
Role and Function: This parameter controls the overall intensity of the AI's creative intervention and enhancement. It dictates how much the original image is altered to achieve the desired outcome, from subtle refinement to a complete re-interpretation.
Current Setting: Apply an AI Strength of ${aiStrength}%.

2. Resemblance: [Value: ${resemblance}%]
Role and Function: This parameter controls how closely the output image's style, color palette, and mood should match a separate uploaded style reference image.
Current Setting: Apply a Resemblance of ${resemblance}%.

3. Clarity: [Value: ${clarity}%]
Role and Function: This parameter adjusts the mid-tone contrast and definition of the image.
Current Setting: Apply a Clarity level of ${clarity}%.

4. Sharpness: [Value: ${sharpnessValue}] (Scale: 0.00 to 1.00)
Role and Function: This parameter controls the enhancement of fine details and edges.
Current Setting: Apply a Sharpness value of ${sharpnessValue}.

5. Geometry Preservation: [Value: 100%]
Role and Function: This parameter dictates that the structural lines, window placements, and overall proportions of the input image must be preserved exactly. Only textures, materials, and lighting should change.
Current Setting: Strict Geometry Preservation (100%).
`;
}

export const planToFurnitureRulesPrompt = `
COLOR-CODED ROOM MAP:
The floor plan image uses distinct fill colors to identify each room zone. 
You MUST use this color legend to determine which zone is which room:
  - Color #123456 = Placeholder

CRITICAL ROOM RULES:
- Each color zone is a SEPARATE room. Treat each colored polygon as its own distinct room.
- Furnish ONLY with furniture appropriate to that specific room type.
- NEVER place bathroom fixtures (toilet, sink, bathtub) in any non-bathroom zone.
- NEVER place kitchen appliances (stove, fridge, counter) in any non-kitchen zone.
- NEVER place beds in living rooms or bathrooms.
- Respect walls (dark lines) as hard boundaries. Furniture must stay within its room zone.
- DOORS (#e78ac3 / Pink): These are door openings. Keep a clear swing clearance arc in front of every door — do NOT block any door with furniture.
- WINDOWS (#a6d854 / Green): These are window openings on the wall. Do NOT place tall furniture (wardrobes, shelving) directly in front of a window. Low furniture (sofa, bench) is acceptable below a window.
- FRONT DOOR (#a63603 / Dark Red): This is the main entrance. Keep the entry path fully clear — no furniture blocking the approach to the front door.
`;

export function constructMaskingProtocol(generationMode: string, outputMode: string, instructions: string, prompt: string): string {
  return `[--TASK FOR SELECTED AREA--]
**Generation Mode:** ${generationMode.toUpperCase()}
**Output Mode:** ${outputMode.toUpperCase()}
**Task Instructions:** ${instructions}
**VIRTUAL MASKING PROTOCOL:**
1.  **GEOMETRY:** Treat the input image as a "Massing Model". Keep the *positions* of windows and the overall building shape.
2.  **SKIN:** Completely WIPE the existing textures, materials, and stylistic details (like arches or cornices).
3.  **RE-SKIN:** Apply the new ${prompt} style logic from scratch onto this massing.
4.  **DOORS:** If no door is visible, YOU MUST CUT A NEW ENTRANCE on the ground floor.
**Output Requirement:** You must strictly output ONLY the generated image. Do not provide any chat conversational text, descriptions, explanations, or preambles.

[--REFERENCE CONTENT--]
If reference images are provided, you must use them as the **GROUND TRUTH** for:
1.  **Geometric Patterns:** Copy the specific frieze/triangle patterns from the references.
2.  **Detaling:** Replicate the exact style of crenellations and openings.
3.  **Authentication:** Do not invent generic patterns; use the provided ones.
`;
}

export const standaloneViewGenerationPrompt = `
[--STANDALONE VIEW GENERATION--]
The input image is a CROPPED SECTION of a larger plan.
**GOAL:** Generate a full, high-quality 3D perspective view derived from this plan section.
**DO NOT** treat this as a masking task. You are generating a new image based on this geometry.
`;

export function constructAxesFirstFloorPrompt(groundFloorJsonString: string): string {
  return `You are an expert AI Architectural Draftsman working exclusively within the "Axes" generative CAD engine.
Your task is to take a provided Ground Floor GeoJSON layout and algorithmically generate ONLY the "First Floor" ("first") geometries. 

[-- STRICT CAD ENGINE CONSTRAINTS --]
1. SCALING & ORIGINS: The environment strictly uses 1 SVG/JSON unit = 1 actual meter. Walls must maintain a strict 0.2m thickness.
2. STAIRCASE OBSTACLE AVOIDANCE: The 'stair' object coordinates are provided in the Ground Floor JSON. You MUST NOT generate any new rooms (e.g., bedrooms, bathrooms, halls) that intersect or overlap with the geometric bounding box of the stair. The stair must remain a clear vertical pathway.
3. EXTERIOR WALLS: The 1st Floor exterior envelope should ideally match the ground floor envelope exactly. You may add minor recesses if architecturally appropriate, but DO NOT exceed the ground floor footprint.
4. FRONT DOORS & CARPORTS: You MUST NOT generate a 'front_door', 'garage', or 'carport' on the First floor. Those are strictly ground-level concepts. 
5. WINDOWS & DOORS: Windows must be placed exclusively on exterior boundary walls. Interior doors must sit within the 0.2m interior partition walls.

[-- FLOOR GENERATION LOGIC --]
FIRST FLOOR ("first"):
- WARNING: DO NOT simply replicate the ground floor. The first floor is a completely new functional zone. 
- Convert public ground floor spaces (living, kitchen) into private residential configurations (multiple bedrooms, bathrooms).
- Include hallways connecting the 'stair' landing area to these new rooms.
- Distribute windows logically along the exterior for the new rooms.
- Make sure there is NO "front_door" or "garage" or "carport".

[-- OUTPUT FORMAT EXPECTATION --]
Return ONLY a strictly valid JSON object. No markdown wrappers, no conversational text. The JSON must exactly match the "categories" mapping style of the input GeoJSON. 
IMPORTANT: DO NOT generate a "roof" key. We handle the roof manually. Generate ONLY the "first" key.
Here is an EXAMPLE structure of what you must return:
{
  "first": {
    "categories": {
      "wall": { "type": "MultiPolygon", "coordinates": [[[[0,0],[10,0],[10,10],[0,10],[0,0]]]] },
      "window": { "type": "MultiLineString", "coordinates": [[[1,0],[3,0]]] },
      "door": { "type": "MultiLineString", "coordinates": [[[4,4],[4,5]]] },
      "bedroom": { "type": "Polygon", "coordinates": [[[[0,0],[4,0],[4,4],[0,4],[0,0]]]] },
      "bathroom": { "type": "Polygon", "coordinates": [[[[4,0],[8,0],[8,4],[4,4],[4,0]]]] }
    }
  }
}

INPUT GROUND FLOOR JSON:
${groundFloorJsonString}`;
}
